import { useQuery, useInfiniteQuery } from '@tanstack/react-query';
import { supabase, toError } from '../supabase';
import { PaperData } from '../../components/papers/types';
import { normalizePaperData } from '../paperAuthors';

const PAGE_SIZE = 20;

export interface ProjectPaperWithSummary {
    projectPaperId: string;
    paper: PaperData;
    paperzillaTitle: string;
    eli5: string;
}

/**
 * Hook to fetch papers with completed summaries for a specific project
 * Uses summary_list_view which has eli5 and title fields directly
 * Returns papers with their AI-generated titles and ELI5 explanations
 */
export function useProjectSummaries(projectId: string | undefined) {
    return useQuery({
        queryKey: ['projectSummaries', projectId],
        queryFn: async () => {
            if (!projectId) throw new Error('No project ID');

            // Join summary_list_view -> paper -> project_paper
            // Start from summaries and join back to find which project they belong to
            // Filter to only papers that were sent (deemed relevant) in the project
            // Note: summary_list_view already filters to is_active=true and status='completed'
            const { data, error } = await supabase
                .from('summary_list_view')
                .select(`
                    title,
                    eli5,
                    paper:paper_id!inner (
                        id,
                        short_id,
                        slug,
                        title,
                        abstract,
                        authors,
                        published_date,
                        pdf_url,
                        url,
                        doi,
                        source_id,
                        source_paper_id,
                        source_paper_version,
                        project_paper!inner (
                            id,
                            project_id,
                            processing_state
                        )
                    )
                `)
                .eq('paper.project_paper.project_id', projectId)
                .eq('paper.project_paper.processing_state', 'sent');

            if (error) throw toError(error);
            if (!data || data.length === 0) return [];

            // Transform to the expected format
            // Data structure: { title, eli5, paper: { id, title, ..., project_paper: [{ id, project_id }] } }
            const result: ProjectPaperWithSummary[] = data
                .filter(item => item.paper && item.paper.project_paper?.[0])
                .map(item => {
                    const paper = item.paper as any;
                    const projectPaper = paper.project_paper[0];

                    return {
                        projectPaperId: projectPaper.id,
                        paper: normalizePaperData({
                            id: paper.id,
                            short_id: paper.short_id,
                            slug: paper.slug,
                            title: paper.title,
                            abstract: paper.abstract,
                            authors: paper.authors,
                            published_date: paper.published_date,
                            pdf_url: paper.pdf_url,
                            url: paper.url,
                            doi: paper.doi,
                            source_id: paper.source_id,
                            source_paper_id: paper.source_paper_id,
                            source_paper_version: paper.source_paper_version,
                        }) as PaperData,
                        paperzillaTitle: item.title || paper.title,
                        eli5: item.eli5 || '',
                    };
                });

            return result;
        },
        enabled: !!projectId,
        staleTime: 1000 * 60 * 5, // 5 minutes
        gcTime: 1000 * 60 * 30,   // Keep in cache for 30 minutes
    });
}

/**
 * Hook to get just the count of papers with summaries for a project
 * Uses paper_summary table for efficiency
 * Useful for showing/hiding the summaries link
 */
/**
 * Infinite-scroll hook for project paper summaries.
 * Same query as useProjectSummaries but with .range() pagination.
 */
export function useProjectSummariesInfinite(
    projectId: string | undefined,
    options: { enabled?: boolean } = {}
) {
    return useInfiniteQuery({
        queryKey: ['projectSummaries-infinite', projectId],
        queryFn: async ({ pageParam = 0, signal }: { pageParam: number; signal?: AbortSignal }) => {
            if (!projectId) throw new Error('No project ID');

            const from = pageParam * PAGE_SIZE;
            const to = from + PAGE_SIZE - 1;

            let query = supabase
                .from('summary_list_view')
                .select(`
                    title,
                    eli5,
                    paper:paper_id!inner (
                        id,
                        short_id,
                        slug,
                        title,
                        abstract,
                        authors,
                        published_date,
                        pdf_url,
                        url,
                        doi,
                        source_id,
                        source_paper_id,
                        source_paper_version,
                        project_paper!inner (
                            id,
                            project_id,
                            processing_state
                        )
                    )
                `)
                .eq('paper.project_paper.project_id', projectId)
                .eq('paper.project_paper.processing_state', 'sent')
                .range(from, to);
            if (signal) {
                query = query.abortSignal(signal);
            }

            const { data, error } = await query;

            if (error) throw toError(error);

            const papers: ProjectPaperWithSummary[] = (data || [])
                .filter(item => item.paper && (item.paper as any).project_paper?.[0])
                .map(item => {
                    const paper = item.paper as any;
                    const projectPaper = paper.project_paper[0];
                    return {
                        projectPaperId: projectPaper.id,
                        paper: normalizePaperData({
                            id: paper.id,
                            short_id: paper.short_id,
                            slug: paper.slug,
                            title: paper.title,
                            abstract: paper.abstract,
                            authors: paper.authors,
                            published_date: paper.published_date,
                            pdf_url: paper.pdf_url,
                            url: paper.url,
                            doi: paper.doi,
                            source_id: paper.source_id,
                            source_paper_id: paper.source_paper_id,
                            source_paper_version: paper.source_paper_version,
                        }) as PaperData,
                        paperzillaTitle: item.title || paper.title,
                        eli5: item.eli5 || '',
                    };
                });

            return { papers, page: pageParam };
        },
        getNextPageParam: (lastPage) =>
            lastPage.papers.length === PAGE_SIZE ? lastPage.page + 1 : undefined,
        initialPageParam: 0,
        enabled: !!projectId && (options.enabled ?? true),
        staleTime: 1000 * 60 * 5,
        gcTime: 1000 * 60 * 30,
    });
}

/**
 * Hook to get just the count of papers with summaries for a project
 * Uses paper_summary table for efficiency
 * Useful for showing/hiding the summaries link
 */
export function useProjectSummariesCount(
    projectId: string | undefined,
    options: { enabled?: boolean } = {}
) {
    return useQuery({
        queryKey: ['projectSummariesCount', projectId],
        queryFn: async ({ signal }) => {
            if (!projectId) return 0;

            // Join summary_list_view -> paper -> project_paper to count summaries for this project
            // Filter to only papers that were sent (deemed relevant) in the project
            // Note: summary_list_view already filters to is_active=true and status='completed'
            let query = supabase
                .from('summary_list_view')
                .select(`
                    summary_id,
                    paper:paper_id!inner (
                        project_paper!inner (
                            project_id,
                            processing_state
                        )
                    )
                `, { count: 'exact', head: true })
                .eq('paper.project_paper.project_id', projectId)
                .eq('paper.project_paper.processing_state', 'sent');
            if (signal) {
                query = query.abortSignal(signal);
            }

            const { count, error } = await query;

            if (error) throw toError(error);

            return count || 0;
        },
        enabled: !!projectId && (options.enabled ?? true),
        staleTime: 1000 * 60 * 5,
        gcTime: 1000 * 60 * 30,
    });
}
